// Public pricing page. Three cards: Free, Hobby, Pro. Reads plan
// details from GET /api/billing/plans so quotas + CTA URLs come
// from the server (no env shape leaks to the client). Highlights the
// caller's current plan when signed in.

import { Tooltip } from "@base-ui-components/react/tooltip";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Check, Info, Loader2, Mail } from "lucide-react";
import { useEffect, useRef } from "react";
import { Link } from "react-router";

import { GlassPanel, Pill, Surface } from "@/components/glass";
import { analytics } from "@/lib/analytics";
import { useSession } from "@/lib/auth-client";

type PlanId = "free" | "lite" | "pro";

interface PlanQuotas {
	asks: number;
	ingests: number;
	ingested_bytes: number;
	pr_reviews: number;
	cost_usd: number;
}

interface PublicPlan {
	id: PlanId;
	price_usd_monthly: number;
	cta: { kind: "subscribe" | "contact" | "current"; url: string | null };
	quotas: PlanQuotas;
}

interface MyBilling {
	plan: { id: PlanId; lockedByAdmin: boolean };
	subscription: { status: string; periodEnd: string | null } | null;
	portalUrl: string | null;
}

export default function PricingView() {
	const session = useSession();
	const plansQ = useQuery({
		queryKey: ["billing-plans"],
		queryFn: async (): Promise<PublicPlan[]> => {
			const r = await fetch("/api/billing/plans");
			if (!r.ok) throw new Error(`plans: ${r.status}`);
			return ((await r.json()) as { plans: PublicPlan[] }).plans;
		},
	});

	const meQ = useQuery({
		queryKey: ["billing-me"],
		queryFn: async (): Promise<MyBilling | null> => {
			const r = await fetch("/api/billing/me", { credentials: "include" });
			if (r.status === 401) return null;
			if (!r.ok) throw new Error(`me: ${r.status}`);
			return r.json();
		},
		enabled: !!session.data?.user,
	});

	const currentPlanId = meQ.data?.plan.id ?? null;
	const signedIn = !!session.data?.user;

	// Spec 39: top of the payment funnel. Fire once auth has settled so the
	// current plan is meaningful.
	const viewedRef = useRef(false);
	useEffect(() => {
		if (viewedRef.current || session.isPending) return;
		viewedRef.current = true;
		analytics.capture("pricing_viewed", {
			current_plan: currentPlanId ?? undefined,
		});
	}, [session.isPending, currentPlanId]);

	return (
		<Tooltip.Provider delay={150} closeDelay={50}>
			<Surface className="min-h-screen px-4 pt-12 pb-24">
				<div className="max-w-[1000px] mx-auto space-y-10">
					<header className="text-center space-y-3">
						<img
							src="/logo.svg"
							alt="renketsu"
							className="h-6 w-auto opacity-95 mx-auto"
						/>
						<h1 className="text-[24px] font-medium tracking-tight">
							Pick a plan
						</h1>
						<p className="text-[13px] text-[var(--color-text-muted)] max-w-md mx-auto">
							Generous limits, predictable pricing. Upgrade when you need more
							headroom.
						</p>
					</header>

					{plansQ.isLoading ? (
						<div className="flex justify-center py-12 text-[var(--color-text-muted)]">
							<Loader2 className="w-4 h-4 animate-spin" />
						</div>
					) : plansQ.error ? (
						<div className="text-center text-[13px] text-[var(--color-danger)]">
							Couldn't load plans: {(plansQ.error as Error).message}
						</div>
					) : (
						<div className="grid md:grid-cols-3 gap-3 max-w-[960px] mx-auto">
							{(plansQ.data ?? []).map((p) => (
								<PlanCard
									key={p.id}
									plan={p}
									isCurrent={currentPlanId === p.id}
									signedIn={signedIn}
								/>
							))}
						</div>
					)}

					<footer className="text-center text-[11px] text-[var(--color-text-dim)] space-x-3">
						<Link to="/" className="hover:text-[var(--color-text-muted)]">
							Back home
						</Link>
						<span>·</span>
						{signedIn ? (
							<Link
								to="/account/usage"
								className="hover:text-[var(--color-text-muted)]"
							>
								Account & usage
							</Link>
						) : (
							<Link
								to="/login"
								className="hover:text-[var(--color-text-muted)]"
							>
								Sign in
							</Link>
						)}
					</footer>
				</div>
			</Surface>
		</Tooltip.Provider>
	);
}

function PlanCard({
	plan,
	isCurrent,
	signedIn,
}: {
	plan: PublicPlan;
	isCurrent: boolean;
	signedIn: boolean;
}) {
	// Lite is the recommended entry point — give it the highlight
	// treatment so the eye lands there. Pro keeps its purple accent for
	// the "real usage" tier.
	const accent =
		plan.id === "lite"
			? "border-[rgba(125,211,252,0.45)] ring-1 ring-[rgba(125,211,252,0.25)]"
			: plan.id === "pro"
				? "border-[rgba(167,139,250,0.35)]"
				: "border-[var(--color-border)]";
	return (
		<GlassPanel
			className={`p-6 space-y-5 border ${accent} ${isCurrent ? "ring-1 ring-white/20" : ""}`}
		>
			<div className="space-y-1.5">
				<div className="flex items-baseline justify-between">
					<div className="text-[12px] uppercase tracking-wider text-[var(--color-text-muted)]">
						{plan.id}
					</div>
					{isCurrent && (
						<span className="text-[10px] uppercase tracking-wider text-[var(--color-success)] border border-[rgba(34,197,94,0.3)] rounded px-1.5 py-0.5">
							Current
						</span>
					)}
				</div>
				<div className="flex items-baseline gap-1">
					<span className="text-[28px] font-medium tracking-tight">
						${plan.price_usd_monthly}
					</span>
					<span className="text-[12px] text-[var(--color-text-dim)]">
						/month
					</span>
				</div>
			</div>

			<div className="space-y-1.5 text-[12px]">
				<Feature
					label="Compute budget"
					value={`$${plan.quotas.cost_usd.toFixed(2)} / month`}
					info="The monthly budget that powers ask, extract, and PR reviews. When it runs out, those operations pause until the next period."
				/>
				<Feature
					label="Storage"
					value={formatBytes(plan.quotas.ingested_bytes)}
					info="Total size of content you've ingested into the graph. New ingestion pauses when full; existing queries keep working."
				/>
				<Feature
					label="Asks"
					value={`${plan.quotas.asks.toLocaleString()} / month`}
				/>
				<Feature
					label="Ingest jobs"
					value={`${plan.quotas.ingests.toLocaleString()} / month`}
				/>
				<Feature label="Webhook events" value="included" />
				<Feature
					label="PR reviews"
					value={
						plan.quotas.pr_reviews === 0
							? "—"
							: `${plan.quotas.pr_reviews} / month`
					}
				/>
			</div>

			<PlanCTA plan={plan} isCurrent={isCurrent} signedIn={signedIn} />
		</GlassPanel>
	);
}

function Feature({
	label,
	value,
	info,
}: {
	label: string;
	value: string;
	info?: string;
}) {
	return (
		<div className="flex items-center gap-2">
			<Check className="w-3 h-3 text-[var(--color-text-dim)] shrink-0" />
			<span className="text-[var(--color-text-muted)] flex-1 flex items-center gap-1">
				{label}
				{info && <FeatureInfo text={info} />}
			</span>
			<span className="text-[var(--color-text)]">{value}</span>
		</div>
	);
}

function FeatureInfo({ text }: { text: string }) {
	return (
		<Tooltip.Root>
			<Tooltip.Trigger
				type="button"
				aria-label="More info"
				className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full text-[var(--color-text-dim)] hover:text-[var(--color-text-muted)] transition-colors"
			>
				<Info className="w-3 h-3" />
			</Tooltip.Trigger>
			<Tooltip.Portal>
				<Tooltip.Positioner sideOffset={6} className="z-50">
					<Tooltip.Popup
						className="rounded-[8px] px-2.5 py-1.5 text-[11px] leading-snug max-w-[220px] text-left"
						style={{
							background: "rgb(20 20 23 / 0.98)",
							backdropFilter: "blur(20px) saturate(180%)",
							WebkitBackdropFilter: "blur(20px) saturate(180%)",
							border: "1px solid rgb(255 255 255 / 0.1)",
							boxShadow:
								"0 12px 40px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.06)",
							color: "var(--color-text)",
						}}
					>
						{text}
					</Tooltip.Popup>
				</Tooltip.Positioner>
			</Tooltip.Portal>
		</Tooltip.Root>
	);
}

function PlanCTA({
	plan,
	isCurrent,
	signedIn,
}: {
	plan: PublicPlan;
	isCurrent: boolean;
	signedIn: boolean;
}) {
	if (isCurrent) {
		return (
			<Pill disabled className="w-full justify-center opacity-60">
				Current plan
			</Pill>
		);
	}

	if (plan.cta.kind === "contact") {
		return (
			<a
				href="mailto:hello@codespring.app?subject=Renketsu%20invite"
				className="block"
				onClick={() =>
					analytics.capture("plan_cta_clicked", {
						plan: plan.id,
						kind: "contact",
					})
				}
			>
				<Pill className="w-full justify-center">
					<Mail className="w-3.5 h-3.5" />
					Request an invite
				</Pill>
			</a>
		);
	}

	if (!plan.cta.url) {
		return (
			<Pill disabled className="w-full justify-center opacity-50">
				Coming soon
			</Pill>
		);
	}

	if (!signedIn) {
		return (
			<Link
				to={`/login?then=${encodeURIComponent(`/pricing?plan=${plan.id}`)}`}
				className="block"
				onClick={() =>
					analytics.capture("plan_cta_clicked", {
						plan: plan.id,
						kind: "subscribe",
					})
				}
			>
				<Pill className="w-full justify-center">
					Sign in to subscribe
					<ArrowRight className="w-3.5 h-3.5" />
				</Pill>
			</Link>
		);
	}

	return (
		<a
			href={plan.cta.url}
			target="_blank"
			rel="noopener noreferrer"
			className="block"
			onClick={() => {
				analytics.capture("plan_cta_clicked", {
					plan: plan.id,
					kind: "subscribe",
				});
				analytics.capture("checkout_redirected", { plan: plan.id });
			}}
		>
			<Pill className="w-full justify-center">
				Subscribe
				<ArrowRight className="w-3.5 h-3.5" />
			</Pill>
		</a>
	);
}

function formatBytes(b: number): string {
	if (b >= 1024 ** 3) return `${(b / 1024 ** 3).toFixed(0)} GB`;
	if (b >= 1024 ** 2) return `${(b / 1024 ** 2).toFixed(0)} MB`;
	if (b >= 1024) return `${(b / 1024).toFixed(0)} KB`;
	return `${b} B`;
}
